import { Button, CardHeader, Typography, Chip } from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";
import {
  Datagrid,
  List,
  NumberField,
  Pagination,
  ReferenceField,
  TextField,
  TopToolbar,
  useDataProvider,
  useListContext,
} from "react-admin";
import TransactionFilter from "../Filters/TransactionFilter";
import { FilterList as FilterListIcon } from "@mui/icons-material";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../firebase";
import _extendLayout from "../../layout/_extendLayout";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Paginations from "../Pagination";
import axios from "axios";
import { Table } from "react-bootstrap";

const CustomActions = ({
  setShowTransactionFilter,
  traEmpExporter,
  appliedFilters,
}) => {
  const getLabel = (key, value) => {
    if (key === "Status") {
      return `Status: ${value}`;
    }
    if (key === "Machine UUID") {
      return `Machine UUID: ${value}`;
    }
    if (key === "Date") {
      return `Date: ${value}`;
    }
    return `${key}: ${value}`;
  };

  return (
    <TopToolbar
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 12,
        width: "100%",
      }}
    >
      <div style={{ display: "flex", alignItems: "center" }}></div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: "flex-end",
          gap: 12,
        }}
      >
        {(appliedFilters || []).map((filter, index) =>
          filter?.value ? (
            <Chip
              key={index}
              label={getLabel(filter.key, filter.value)}
              color="primary"
            />
          ) : null
        )}
        <Button
          startIcon={<FilterListIcon />}
          variant="outlined"
          color="primary"
          style={{ border: "none" }}
          onClick={() => setShowTransactionFilter(true)}
        >
          Show Filters
        </Button>
        <Button
          color="primary"
          startIcon={<FileDownloadIcon />}
          onClick={traEmpExporter}
        >
          Export
        </Button>
      </div>
    </TopToolbar>
  );
};

const TransactionEmployeeList = (props) => {
  const dataProvider = useDataProvider();

  const [isAdmin, setIsAdmin] = useState(false);
  const [transEmpList, setTransEmpList] = useState([]);
  const [clientId, setClientId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mroSortField, setMroSortField] = useState("Date");
  const [mroSortOrder, setMroSortOrder] = useState("DESC");
  const [mroPage, setMroPage] = useState(1);
  const [showTransactionFilter, setShowTransactionFilter] = useState(false);
  const [mroFilters, setMroFilters] = useState({});
  const [perPage] = useState(25);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const checkAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const { filter: currentFilter, admin } = await getCurrentUser(user.uid);
      }
    });

    return () => checkAuth();
  }, []);

  const getCurrentUser = async (userId) => {
    try {
      const res = await dataProvider.getOne("users", { id: userId });

      const admin = res.data.role === "admin";
      setIsAdmin(admin);

      let currentFilter = admin
        ? { mode: "mro" }
        : {
            client: res.data?.client?.id,
            mode: "mro",
          };

      if (!admin) setClientId(res.data?.client?.id);

      return { filter: currentFilter, admin };
    } catch (err) {
      console.log(err);
    }
  };

  const getTransactionByEmployee = async (filter, pagination, sort) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      let skip = (pagination.page - 1) * pagination.perPage;
      let limit = pagination.perPage;
      let sortField = sort.field || "Date";
      let sortOrder = sort.order || "DESC";
      let params = {
        ...filter,
        skip,
        limit: 2000,
        sortField: sortField,
        sortOrder: sortOrder,
      };
      params.mode = "mro";

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/machine_transaction/mroListByEmployee`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const rawResult = res.data.result.data;
      const total = res.data.result.total.length;
      if (Array.isArray(rawResult)) {
        const grouped = groupByEmployee(rawResult);
        const startIndex = (pagination.page - 1) * pagination.perPage;
        const endIndex = startIndex + pagination.perPage;
        const paginatedEmployees = grouped.slice(startIndex, endIndex);
        setFilteredData(paginatedEmployees);
        setGroupedData(grouped);
        setTransEmpList(grouped);
        setTotal(grouped.length);
      } else {
        console.error("Invalid data format:", rawResult);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const groupByEmployee = (transactions) => {
    const grouped = transactions.reduce((acc, transaction) => {
      const employeeName = transaction.employee?.fullName;
      if (!employeeName) return acc;

      if (!acc[employeeName]) {
        acc[employeeName] = {
          employee: transaction.employee,
          client: transaction.client,
          date: transaction.date,
          transactionId: transaction.transactionId,
          companyName: transaction.companyName,
          totalQty: 0,
          totalTransaction: 0,
          totalCredit: 0,
        };
      }

      const qty = Number(transaction.totalQty) || 0;
      const totalTransaction = Number(transaction.totalTransaction) || 0;
      const revenue = Number(transaction.totalRevenue) || 0;

      acc[employeeName].totalQty += qty;
      acc[employeeName].totalTransaction += totalTransaction;
      acc[employeeName].totalCredit += revenue;

      return acc;
    }, {});

    return Object.values(grouped).map((item) => ({
      ...item,
      totalCredit: Number(item.totalCredit.toFixed(2)),
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      let filter = isAdmin
        ? { mode: "mro" }
        : { client: clientId, mode: "mro" };

      await getTransactionByEmployee(
        mroFilters,
        { page: mroPage, perPage: perPage },
        { field: mroSortField, order: mroSortOrder }
      );
    };

    fetchData();
  }, [mroPage, mroSortField, mroSortOrder]);

  const traEmpExporter = async (datas) => {
    const headers = [
      "ID",
      "Client",
      "Employee Active",
      "Employee Cost Center",
      "Employee Credit",
      "Employee Number",
      "Employee Full Name",
      "Employee ID",
      "Total Quantity",
      "Total Transactions",
      "Total Cash",
      "Total Credit",
      "Employee Params",
    ];

    const csvRows = [headers.map((h) => `"${h}"`).join(",")];

    // const filterData = datas.filter((e) => e.client === clientId);

    Object.values(datas).forEach((e) => {
      // const tag = Array.isArray(e.employee?.params)
      //     ? e.employee.params.map((p) => p.tag ?? "").join(", ")
      //     : "";

      // const value = Array.isArray(e.employee?.params)
      //     ? e.employee.params.map((p) => p.value ?? "").join(", ")
      //     : "";

      const formattedParams = Array.isArray(e.employee?.params)
        ? e.employee.params
            .map((p) => `tag: ${p.tag ?? ""}, value: ${p.value ?? ""}`)
            .join("; ")
        : "";

      const quotedFormattedParams = `"${formattedParams}"`;

      const commonDetails = [
        e.transactionId,
        e.client ?? "",
        e.employee?.active ?? "",
        e.employee?.costCenter ?? "",
        e.employee?.credit ?? "",
        e.employee?.employeeNumber ?? "",
        e.employee?.fullName ?? "",
        e.employee?.id ?? "",
        // tag,
        // value,
        e.totalQty,
        e.totalTransaction,
        e.totalCash ?? "0",
        e.totalCredit,
        quotedFormattedParams,
      ];
      csvRows.push(
        commonDetails
          .map((field) => `"${String(field).replace(/"/g, '""')}"`)
          .join(",")
      );
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "MRO Report Employees.csv";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSortChange = (field) => {
    setMroSortField(field);
    setMroSortOrder((prevSortOrder) => {
      const newOrder = prevSortOrder === "ASC" ? "DESC" : "ASC";
      return newOrder;
    });
  };

  return (
    <>
      <_extendLayout isModalOpen={isModalOpen || showTransactionFilter}>
        <div style={{ width: "90%" }}>
          <CardHeader title="MRO List By Employee" style={{ paddingLeft: 0 }} />
          {loading ? (
            <Typography>Loading...</Typography>
          ) : transEmpList.length == 0 ? (
            <Typography>No data found</Typography>
          ) : (
            <>
              <CustomActions
                traEmpExporter={() => traEmpExporter(filteredData)}
                setFilteredData={setFilteredData}
                setShowTransactionFilter={setShowTransactionFilter}
                data={transEmpList}
              />
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th
                      className="fixed-col-name"
                      onClick={() => handleSortChange("Employee.fullName")}
                      style={{ cursor: "pointer" }}
                    >
                      Employee Name{" "}
                      {mroSortField === "Employee.fullName" &&
                        (mroSortOrder === "ASC" ? "↑" : "↓")}
                    </th>
                    {isAdmin && <th>Company Name</th>}
                    <th
                      className="fixed-col-transaction"
                      onClick={() => handleSortChange("totalTransactions")}
                      style={{ cursor: "pointer" }}
                    >
                      Total Transaction{" "}
                      {mroSortField === "totalTransactions" &&
                        (mroSortOrder === "ASC" ? "↑" : "↓")}
                    </th>
                    <th
                      className="fixed-col-transaction"
                      onClick={() => handleSortChange("totalRevenue")}
                      style={{ cursor: "pointer" }}
                    >
                      Total Spending (Credits){" "}
                      {mroSortField === "totalRevenue" &&
                        (mroSortOrder === "ASC" ? "↑" : "↓")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={isAdmin ? 4 : 3}
                        style={{ textAlign: "center", padding: "1rem" }}
                      >
                        No data found
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((item) => (
                      <tr key={item.transactionId}>
                        <td>{item.employee.fullName}</td>
                        {isAdmin && <td>{item.companyName}</td>}
                        <td>{item.totalTransaction}</td>
                        <td>{item.totalCredit}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Paginations
                  total={total}
                  currentPage={mroPage}
                  perPage={perPage}
                  onPageChange={setMroPage}
                />
              </div>
            </>
          )}
        </div>

        {showTransactionFilter && (
          <TransactionFilter
            storageKey="transaction-employee-filters"
            setFilteredData={setFilteredData}
            allData={transEmpList}
            modalOpen={showTransactionFilter}
            onModalOpenChange={setShowTransactionFilter}
            setTotal={setTotal}
            setFilter={setMroFilters}
            setIsLoading={setLoading}
            sales={{ sales: false, module: "employee" }}
          />
        )}
      </_extendLayout>
    </>
  );
};

export default TransactionEmployeeList;
